import { useCallback, useEffect, useState } from "react";
import { X, Download, PlusCircle, Loader2, FileQuestion, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kindOf = (ct = "") => {
  if (ct.startsWith("image/")) return "image";
  if (ct === "application/pdf") return "pdf";
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("audio/")) return "audio";
  if (ct.startsWith("text/")) return "text";
  return "other";
};

/** Full preview without downloading first — images, PDFs, video and audio inline. */
export const AssetLightbox = ({ assets, index, authHeader, onClose, onLog, onGrabbed }) => {
  const [at, setAt] = useState(index);
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const asset = assets[at];
  const kind = kindOf(asset?.content_type);

  const step = useCallback((dir) => {
    setAt((v) => (v + dir + assets.length) % assets.length);
  }, [assets.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, step]);

  useEffect(() => {
    if (!asset || kind === "other") {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setSrc(null);
    (async () => {
      try {
        const resp = await fetch(`${API}/library/assets/${asset.id}/preview`, { headers: authHeader() });
        if (!resp.ok) throw new Error("preview failed");
        const b = await resp.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(b);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setSrc(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset, kind, authHeader]);

  const download = async () => {
    try {
      const resp = await fetch(`${API}/library/assets/${asset.id}/download`, { headers: authHeader() });
      if (!resp.ok) throw new Error("Download failed");
      const b = await resp.blob();
      const href = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = href;
      a.download = asset.filename || "asset";
      a.click();
      URL.revokeObjectURL(href);
      onGrabbed?.(asset);
    } catch {
      toast.error("Couldn't download that one.");
    }
  };

  if (!asset) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-navy-deep/95 backdrop-blur-sm flex flex-col"
      data-testid="asset-lightbox"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex items-start justify-between gap-4 px-5 sm:px-8 py-4 text-white">
        <div className="min-w-0">
          <p className="font-heading font-medium text-lg truncate">{asset.filename}</p>
          <p className="text-xs text-white/50">
            {asset.description || asset.content_type}
            {assets.length > 1 ? ` · ${at + 1} of ${assets.length}` : ""}
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          aria-label="Close preview"
          className="shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X size={17} />
        </button>
      </div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center px-5 sm:px-8">
        {assets.length > 1 && (
          <>
            <button
              onClick={() => step(-1)}
              data-testid="lightbox-prev"
              aria-label="Previous asset"
              className="absolute left-2 sm:left-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={19} />
            </button>
            <button
              onClick={() => step(1)}
              data-testid="lightbox-next"
              aria-label="Next asset"
              className="absolute right-2 sm:right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight size={19} />
            </button>
          </>
        )}

        {loading ? (
          <Loader2 className="w-7 h-7 animate-spin text-white/60" />
        ) : kind === "image" && src ? (
          <img src={src} alt={asset.filename} data-testid="lightbox-image" className="max-h-full max-w-full object-contain rounded-xl" />
        ) : kind === "pdf" && src ? (
          <iframe src={src} title={asset.filename} data-testid="lightbox-pdf" className="w-full h-full rounded-xl bg-white" />
        ) : kind === "video" && src ? (
          <video src={src} controls data-testid="lightbox-video" className="max-h-full max-w-full rounded-xl bg-black" />
        ) : kind === "audio" && src ? (
          <audio src={src} controls data-testid="lightbox-audio" className="w-full max-w-lg" />
        ) : kind === "text" && src ? (
          <iframe src={src} title={asset.filename} data-testid="lightbox-text" className="w-full h-full rounded-xl bg-white" />
        ) : (
          <div className="text-center text-white/70" data-testid="lightbox-no-preview">
            <FileQuestion size={34} className="mx-auto opacity-60" />
            <p className="mt-3 text-sm">
              This one can't be previewed in the browser — download it to open it.
            </p>
          </div>
        )}
      </div>

      <div className="px-5 sm:px-8 py-4 flex flex-wrap gap-2.5">
        <button
          onClick={download}
          data-testid="lightbox-download"
          className="inline-flex items-center gap-2 rounded-full bg-lime text-navy-deep font-bold text-sm px-5 py-2.5 hover:brightness-105 transition-all"
        >
          <Download size={15} /> Download
        </button>
        {onLog && (
          <button
            onClick={() => onLog(asset)}
            data-testid="lightbox-log"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white font-semibold text-sm px-5 py-2.5 hover:bg-white/10 transition-colors"
          >
            <PlusCircle size={15} /> Log this
          </button>
        )}
      </div>
    </div>
  );
};

export default AssetLightbox;
